import type { CalendarMode } from '../define.js';
import type { CalendarEvent } from './calendar-view-types.js';

export type CalendarDisplayProperty = 'attendees' | 'location' | 'status';
export type CalendarSourceIdentityMode = 'always' | 'auto' | 'never';

export interface CalendarDisplaySettings {
  properties: Record<CalendarMode, readonly CalendarDisplayProperty[]>;
  sourceIdentityMode: CalendarSourceIdentityMode;
  version: 1;
}

export interface CalendarEventDisplayValue {
  ariaLabel: string;
  value: string;
}

export const DEFAULT_CALENDAR_DISPLAY_SETTINGS: CalendarDisplaySettings = {
  properties: {
    day: ['location', 'status'],
    month: [],
    week: ['location'],
  },
  sourceIdentityMode: 'auto',
  version: 1,
};

const LIMIT: Record<CalendarMode, number> = { day: 2, month: 0, week: 1 };
const ORDER: readonly CalendarDisplayProperty[] = [
  'location',
  'attendees',
  'status',
];

export function decodeCalendarDisplaySettings(
  value: unknown
): CalendarDisplaySettings | null {
  if (!isExactRecord(value, ['properties', 'sourceIdentityMode', 'version']))
    return null;
  if (value.version !== 1) return null;
  if (!isExactRecord(value.properties, ['day', 'month', 'week'])) return null;
  const sourceIdentityMode = value.sourceIdentityMode;
  if (
    sourceIdentityMode !== 'always' &&
    sourceIdentityMode !== 'never' &&
    sourceIdentityMode !== 'auto'
  )
    return null;
  const day = decodeProperties(value.properties.day, 'day');
  const week = decodeProperties(value.properties.week, 'week');
  const month = decodeProperties(value.properties.month, 'month');
  if (!day || !week || !month) return null;
  return {
    properties: { day, month, week },
    sourceIdentityMode,
    version: 1,
  };
}

export function normalizeCalendarDisplaySettings(
  value: unknown
): CalendarDisplaySettings {
  return (
    decodeCalendarDisplaySettings(value) ?? DEFAULT_CALENDAR_DISPLAY_SETTINGS
  );
}

export function calendarSourceIdentityVisible(
  mode: CalendarSourceIdentityMode,
  sourceCount: number
): boolean {
  return mode === 'always' || (mode === 'auto' && sourceCount > 1);
}

export function calendarEventDisplayValues(
  event: CalendarEvent,
  mode: CalendarMode,
  settings: CalendarDisplaySettings
): CalendarEventDisplayValue[] {
  return settings.properties[mode]
    .slice(0, LIMIT[mode])
    .map(property => displayValue(event, property))
    .filter((value): value is CalendarEventDisplayValue => Boolean(value));
}

export function calendarEventAccessibleText(
  event: CalendarEvent,
  mode: CalendarMode,
  settings: CalendarDisplaySettings,
  sourceCount: number,
  timeLabel: string
): string {
  const showSource = calendarSourceIdentityVisible(
    settings.sourceIdentityMode,
    sourceCount
  );
  return [
    event.title,
    showSource ? event.sourceFullIdentityLabel : '',
    timeLabel,
    ...calendarEventDisplayValues(event, mode, settings).map(
      value => value.ariaLabel
    ),
  ]
    .filter(Boolean)
    .join('\n');
}

function decodeProperties(
  value: unknown,
  mode: CalendarMode
): CalendarDisplayProperty[] | null {
  if (!Array.isArray(value) || value.length > LIMIT[mode]) return null;
  const selected: CalendarDisplayProperty[] = [];
  for (const item of value) {
    if (!ORDER.includes(item as CalendarDisplayProperty)) return null;
    if (selected.includes(item as CalendarDisplayProperty)) return null;
    selected.push(item as CalendarDisplayProperty);
  }
  return selected;
}

function displayValue(
  event: CalendarEvent,
  property: CalendarDisplayProperty
): CalendarEventDisplayValue | null {
  if (property === 'location')
    return textValue(event.locationPhysical || event.locationVirtual);
  if (property === 'status') return textValue(event.status.trim());
  if (event.attendeeCount <= 0) return null;
  const noun = event.attendeeCount === 1 ? 'attendee' : 'attendees';
  return {
    ariaLabel:
      event.attendeeAriaLabel?.trim() || `${event.attendeeCount} ${noun}`,
    value:
      event.attendeeDisplayLabel?.trim() ||
      (event.attendeeCount >= 100
        ? '99+ attendees'
        : `${event.attendeeCount} ${noun}`),
  };
}

function textValue(value: string): CalendarEventDisplayValue | null {
  const normalized = value.trim();
  return normalized ? { ariaLabel: normalized, value: normalized } : null;
}

function isExactRecord(
  value: unknown,
  keys: readonly string[]
): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}
